/**
 * Sync Folder Action
 * Triggers synchronization for SharePoint, Teams, OneNote, or Box folders.
 * Also supports syncing files (syncs the file's parent folder).
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext } from "./types";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { isInSyncedFolder, isSftpSyncableDriveRoot } from "../specialFolders";
import {
  clearProductInterval,
  setProductInterval,
} from "@/utils/productTimers";

type SyncType = "ms-teams" | "ms-sharepoint" | "ms-onenote" | "box" | null;

/**
 * Determine the sync type based on path. `sharepointRootNames` carries the
 * org's named per-site roots (#11713), lowercased, from the drive policy —
 * a folder under one is SharePoint-synced exactly like the literal root.
 */
function getSyncType(
  path: string[],
  sharepointRootNames: readonly string[]
): SyncType {
  if (path.length === 0) return null;

  const checkPath = (segment: string): SyncType => {
    const lower = segment.toLowerCase();
    if (lower === "onenote" || lower.includes("onenote")) return "ms-onenote";
    if (lower === "sharepoint" || lower.includes("sharepoint"))
      return "ms-sharepoint";
    if (
      lower === "teams channels" ||
      lower === "channels" ||
      lower.includes("channels")
    )
      return "ms-teams";
    if (lower === "box") return "box";
    if (sharepointRootNames.includes(lower)) return "ms-sharepoint";
    return null;
  };

  // Check first position
  const firstResult = checkPath(path[0]);
  if (firstResult) return firstResult;

  // Check second position (for orgs with drives enabled)
  if (path.length > 1) {
    const secondResult = checkPath(path[1]);
    if (secondResult) return secondResult;
  }

  return null;
}

/**
 * Poll the tree after a sync has been accepted, so newly-ingested files appear
 * without the user reloading. Shared by every connector's sync path.
 */
function refreshTreeWhileSyncing(context: ActionContext): void {
  let refreshCount = 0;
  const maxRefreshes = 12; // 3 minutes at 15-second intervals

  const refreshInterval = setProductInterval(() => {
    refreshCount++;
    context.refreshTree();

    if (refreshCount >= maxRefreshes) {
      clearProductInterval(refreshInterval);
    }
  }, 15000);
}

/**
 * Sync an SFTP-backed shared drive (#10860).
 *
 * Whole-drive by necessity — the SFTP syncer refuses a partial sync — so the
 * confirmation says so plainly and no folder path is sent.
 */
async function syncSftpDrive(
  targetDatum: Datum,
  context: ActionContext
): Promise<void> {
  const driveName = targetDatum.name;
  const message = `This will refresh the entire "${driveName}" drive. New and updated files may take a few minutes to appear.`;
  const confirmed = await context.showConfirmation?.({
    title: "Sync Drive",
    message,
    confirmText: "Sync",
    cancelText: "Cancel",
  });
  if (!confirmed) return;

  try {
    const response = await fpFetch(
      `${BASE_API_URL_V2}/api/v2/connections/sftp/synchronize`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );

    if (response.ok) {
      context.showToast?.(`Sync started for "${driveName}"`, "success");
      refreshTreeWhileSyncing(context);
    } else {
      context.showToast?.(`Failed to sync "${driveName}"`, "error");
    }
  } catch (error) {
    console.error("syncFolder: Error syncing SFTP drive", error);
    context.showToast?.(`Error syncing "${driveName}"`, "error");
  }
}

/**
 * Sync a folder or file with its remote source.
 * If called on a file, syncs the file's parent folder.
 */
export async function syncFolder(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  // If it's a file, we sync its parent folder
  const targetDatum = datum.type === "file" ? datum.parent : datum;

  if (!targetDatum || targetDatum.type !== "folder") {
    context.showToast?.("Unable to determine folder to sync", "error");
    return;
  }

  if (isSftpSyncableDriveRoot(targetDatum, context)) {
    await syncSftpDrive(targetDatum, context);
    return;
  }

  const syncType = getSyncType(
    targetDatum.path,
    context.sharepointRootNames ?? []
  );
  if (!syncType) {
    context.showToast?.("This is not a synced folder", "error");
    return;
  }

  // Confirm with user
  const folderName = targetDatum.name;
  const message =
    datum.type === "file"
      ? `This will refresh all documents in the parent folder "${folderName}".`
      : `This will refresh all documents in "${folderName}".`;
  const confirmed = context.showConfirmation
    ? await context.showConfirmation({
        title: "Sync Folder",
        message,
        confirmText: "Sync",
        cancelText: "Cancel",
      })
    : window.confirm(message);
  if (!confirmed) return;

  try {
    // Use targetDatum.path directly - it's already an array
    // This preserves "/" characters in folder names correctly
    // Strip drive prefix if in across-drives mode with drives enabled
    const pathArray = context.filetree?.shouldStripDrivePrefix
      ? targetDatum.path.slice(1)
      : [...targetDatum.path];

    // Find the connection prefix index by checking segment names
    const prefixes: Record<string, string> = {
      onenote: "OneNote",
      sharepoint: "Sharepoint",
      "teams channels": "Teams Channels",
      channels: "Teams Channels",
      box: "Box",
    };

    let prefixIndex = -1;
    for (let i = 0; i < Math.min(pathArray.length, 2); i++) {
      const segmentLower = pathArray[i].toLowerCase();
      if (
        Object.keys(prefixes).some((p) => segmentLower === p) ||
        // Named per-site roots (#11713): the backend expects a site-relative
        // folder_path, so the root segment strips off exactly like the
        // literal "Sharepoint".
        (context.sharepointRootNames ?? []).includes(segmentLower)
      ) {
        prefixIndex = i;
        break;
      }
    }

    // Get the path after the connection prefix (or empty if at root)
    const pathToSyncList =
      prefixIndex >= 0 ? pathArray.slice(prefixIndex + 1) : [];

    // Determine endpoint based on sync type
    let endpoint = "";
    if (syncType === "ms-onenote") {
      endpoint = `${BASE_API_URL_V2}/api/v2/connections/onenote/synchronize`;
    } else if (syncType === "ms-sharepoint") {
      endpoint = `${BASE_API_URL_V2}/api/v2/connections/sharepoint/synchronize`;
    } else if (syncType === "ms-teams") {
      endpoint = `${BASE_API_URL_V2}/api/v2/connections/teams/synchronize`;
    } else if (syncType === "box") {
      endpoint = `${BASE_API_URL_V2}/api/v2/connections/box/synchronize`;
    }

    // Prepare request body
    const requestBody: { folder_path: string[] | null; drive_id?: string } = {
      folder_path: pathToSyncList.length > 0 ? pathToSyncList : null,
    };

    // Include drive_id if available
    const driveId = targetDatum.payload?.driveId || context.driveId;
    if (driveId) {
      requestBody.drive_id = driveId;
    }

    // Send sync request
    const response = await fpFetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.ok) {
      context.showToast?.(
        `Sync started for folder "${targetDatum.name}"`,
        "success"
      );

      // Set up periodic refreshes to catch newly synced files
      refreshTreeWhileSyncing(context);
    } else {
      context.showToast?.(
        `Failed to sync folder "${targetDatum.name}"`,
        "error"
      );
    }
  } catch (error) {
    console.error("syncFolder: Error syncing folder", error);
    context.showToast?.(`Error syncing folder "${targetDatum.name}"`, "error");
  }
}

/**
 * Action definition for context menu.
 * Works for both files and folders in synced folders.
 */
export const syncFolderAction: ActionDefinition = {
  config: {
    action: "sync-folder",
    label: "Sync",
  },
  // SFTP-backed drives are root-only (#10860); every other connector stays
  // applicable throughout its subtree.
  //
  // Two capabilities are required rather than assumed. A confirmation channel:
  // the SFTP branch treats a missing one as "declined" and returns, so without
  // it the menu item does nothing at all. And, for a file, a live parent link:
  // syncing a file means syncing its parent folder, which is resolved through
  // `datum.parent` — scratch state only the tree populates. Surfaces built from
  // standalone datums (search results) therefore do not offer Sync.
  isApplicable: (datum, context) =>
    (isInSyncedFolder(datum, context) ||
      isSftpSyncableDriveRoot(datum, context)) &&
    Boolean(context?.showConfirmation) &&
    (datum.type !== "file" || Boolean(datum.parent)),
  execute: syncFolder,
};
